import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import authService from '../../services/authService';
import { getApiErrorMessage, logError } from '../../utils/apiError';
import AdminHeader from '../../components/admin/AdminHeader';

const OrganizationsPage = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('OrganizationsPage - Component mounted');
    
    // Check if user is logged in as admin using authService
    if (!authService.isAdminLoggedIn()) {
      console.log('OrganizationsPage - Not authenticated, redirecting to login');
      navigate('/admin/login');
      return;
    }
    
    // Get admin user data from authService
    const userData = authService.getAdminUser();
    
    if (!userData) {
      console.log('OrganizationsPage - No admin user data, redirecting to login');
      navigate('/admin/login');
      return;
    }
    
    console.log('OrganizationsPage - User data:', userData);
    setAdminUser(userData);
  }, [navigate]);

  useEffect(() => {
    if (!adminUser) return;
    
    console.log('OrganizationsPage - Fetching organizations');
    setLoading(true);
    
    api.getOrganizations()
      .then(response => {
        console.log('OrganizationsPage - Organizations fetched:', response.data);
        // Use the correct response format from the API
        const organizationData = response.data.orgs || [];
        setOrgs(Array.isArray(organizationData) ? organizationData : []);
        setLoading(false);
      })
      .catch(err => {
        const errorMessage = getApiErrorMessage(err);
        logError('OrganizationsPage - Fetch error', err);
        setError(`Failed to fetch organizations: ${errorMessage}`);
        setLoading(false);
        
        // Check for auth errors
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          console.error('Authentication error:', err.response.status);
          
          // Clear credentials and redirect to login using authService
          authService.adminLogout();
          navigate('/admin/login');
        }
      });
  }, [adminUser, navigate]);

  if (loading) return <div>Loading organizations...</div>;
  if (error) return <div className="error-message" style={{ color: 'red', padding: '10px', backgroundColor: '#ffeeee', borderRadius: '4px' }}>{error}</div>;

  return (
    <div>
      <AdminHeader adminUser={adminUser} />
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '20px' 
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '28px', 
          color: '#111827' 
        }}>
          Organizations
        </h2>
        <p style={{ 
          margin: '0 0 24px 0', 
          fontSize: '14px', 
          color: '#6b7280' 
        }}>
          Manage organizations across your platform
        </p>

        {orgs.length === 0 ? (
          <div style={{ 
            padding: '40px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px', 
            textAlign: 'center',
            border: '1px solid #e5e7eb'
          }}>
            No organizations found.
          </div>
        ) : (
          <div style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Name</th>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Status</th>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>External App</th>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>External Org ID</th>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Created At</th>
                  <th style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #e5e7eb', 
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((org) => (
                  <tr key={org._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ 
                      padding: '16px', 
                      fontSize: '14px',
                      color: '#111827',
                      fontWeight: '500'
                    }}>{org.name}</td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '100px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: '#dcfce7',
                        color: '#166534'
                      }}>
                        {org.status || 'active'}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '16px',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>{org.external?.app || '-'}</td>
                    <td style={{ 
                      padding: '16px',
                      fontSize: '14px',
                      color: '#6b7280',
                      fontFamily: 'monospace'
                    }}>{org.external?.rsvpxOrgId || '-'}</td>
                    <td style={{ 
                      padding: '16px',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>{new Date(org.createdAt).toLocaleString()}</td>
                    <td style={{ padding: '16px' }}>
                      <button
                        onClick={() => navigate(`/admin/organizations/${org._id}/manage`)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#2563eb',
                          backgroundColor: 'white',
                          border: '1px solid #2563eb',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#2563eb';
                          e.target.style.color = 'white';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.color = '#2563eb';
                        }}
                      >
                        Manage →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationsPage;
